import React, { useState, useEffect } from 'react';
import {
    Box, Typography, TextField, Button,
    Paper, CircularProgress, Alert,
    Chip, InputAdornment, Link, useTheme
} from '@mui/material';
import {
    Business as BrandIcon,
    Add as AddIcon,
    Close as CloseIcon,
    CheckCircle as CheckIcon,
    ArrowForward as NextIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme as useDesignTheme } from '../../design-system/providers/ThemeProvider';
import { ReactComponent as MensureLogo } from '../../assets/logo.svg';
import api from '../../services/api';

const TIER_OPTIONS = [
    { value: 'small', label: 'Pequena', desc: 'Startups e marcas regionais' },
    { value: 'medium', label: 'Média', desc: 'Empresas com presença nacional' },
    { value: 'large', label: 'Grande', desc: 'Grandes corporações e marcas nacionais' }
];

const OnboardingPage = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const { primaryGradient } = useDesignTheme();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [done, setDone] = useState(false);
    const { user, updateUser } = useAuth();

    // Redirecionar se já tem marca ou é admin
    useEffect(() => {
        if (!user) return;
        const BYPASS_ROLES = ['admin', 'mensure', 'curator', 'curador'];
        const isBypass = BYPASS_ROLES.includes(user.role?.toLowerCase());
        if (isBypass || user.hasBrand) {
            navigate('/', { replace: true });
        }
    }, [user, navigate]);

    const [brandName, setBrandName] = useState('');
    const [tierProfile, setTierProfile] = useState('medium');
    const [keywordInput, setKeywordInput] = useState('');
    const [keywords, setKeywords] = useState([]);

    const handleAddKeyword = () => {
        const kw = keywordInput.trim().toLowerCase();
        if (kw && keywords.length < 10 && !keywords.includes(kw)) {
            setKeywords(prev => [...prev, kw]);
            setKeywordInput('');
        }
    };

    const handleRemoveKeyword = (kw) => {
        setKeywords(prev => prev.filter(k => k !== kw));
    };

    const handleSubmit = async () => {
        if (!brandName.trim()) { setError('Nome da marca é obrigatório'); return; }
        setLoading(true);
        setError('');
        try {
            const brandRes = await api.post('/brands', { name: brandName.trim(), tierProfile });
            const brandId = brandRes.data.data?._id || brandRes.data._id;

            if (keywords.length > 0 && brandId) {
                await api.post('/briefings', {
                    name: brandName.trim(),
                    brandId,
                    primaryKeywords: keywords.map(kw => ({ keyword: kw, weight: 5 })),
                    settings: { autoAnalyze: true, sensitivity: 'medium', useAI: true }
                }).catch(() => { });
            }

            if (user && updateUser) {
                updateUser({ ...user, hasBrand: true });
            }

            setDone(true);
            setTimeout(() => {
                navigate('/');
            }, 1500);
        } catch (err) {
            const msg = err.response?.data?.message || '';
            if (err.response?.status === 400 && msg.toLowerCase().includes('ja existe')) {
                if (user && updateUser) {
                    updateUser({ ...user, hasBrand: true });
                }
                setDone(true);
                setTimeout(() => {
                    navigate('/');
                }, 1500);
                return;
            }
            setError(msg || 'Erro ao criar marca. Tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    const selectedColor = theme.palette.primary.main;

    return (
        <Box
            sx={{
                minHeight: '100vh',
                width: '100%',
                backgroundColor: 'background.paper',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: { xs: 1.5, sm: 2 },
                boxSizing: 'border-box',
                overflowX: 'hidden',
            }}
        >
            <Paper
                elevation={2}
                sx={{
                    padding: { xs: 2, sm: 3 },
                    width: '100%',
                    maxWidth: { xs: '100%', sm: 440 },
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    backgroundColor: 'background.paper',
                    borderRadius: 2,
                    boxSizing: 'border-box',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                }}
            >
                {/* Logo */}
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                    <MensureLogo style={{
                        height: 36,
                        width: 'auto',
                        fill: theme.palette.primary.main,
                        color: theme.palette.primary.main,
                    }} />
                </Box>

                {done ? (
                    <Box textAlign="center" py={3} width="100%">
                        <Box sx={{
                            width: 56, height: 56, borderRadius: '50%',
                            bgcolor: 'success.light', display: 'flex',
                            alignItems: 'center', justifyContent: 'center',
                            mx: 'auto', mb: 2, opacity: 0.9
                        }}>
                            <CheckIcon sx={{ fontSize: 30, color: 'success.main' }} />
                        </Box>
                        <Typography variant="h6" fontWeight={700} color="success.main" mb={0.5}>
                            Pronto!
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Sua marca foi criada. Entrando na plataforma...
                        </Typography>
                        <CircularProgress size={18} sx={{ mt: 2, color: 'success.main' }} />
                    </Box>
                ) : (
                    <Box sx={{ width: '100%' }}>
                        <Typography
                            component="h1"
                            variant="h5"
                            sx={{ mb: 0.5, fontWeight: 600, color: 'text.primary', textAlign: 'center' }}
                        >
                            Configure sua Marca
                        </Typography>
                        <Typography variant="body2" color="text.secondary" textAlign="center" mb={3}>
                            Antes de acessar a plataforma, cadastre a marca que você vai monitorar
                        </Typography>

                        {error && (
                            <Alert severity="error" sx={{ mb: 2, borderRadius: 1.5 }}>
                                {error}
                            </Alert>
                        )}

                        {/* Nome da Marca */}
                        <Box mb={2.5}>
                            <Typography variant="caption" fontWeight={600} color="text.secondary"
                                sx={{ mb: 0.5, display: 'block', letterSpacing: 0.3 }}>
                                NOME DA MARCA *
                            </Typography>
                            <TextField
                                fullWidth
                                placeholder="Ex: Mensure, BMW Brasil, Michael Page..."
                                value={brandName}
                                onChange={e => setBrandName(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <BrandIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                                        </InputAdornment>
                                    )
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 1.5,
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'primary.main',
                                        }
                                    },
                                    '& .MuiOutlinedInput-notchedOutline legend': { display: 'none' },
                                    '& .MuiOutlinedInput-notchedOutline': { top: 0 },
                                }}
                            />
                        </Box>

                        {/* Porte da Marca */}
                        <Box mb={2.5}>
                            <Typography variant="caption" fontWeight={600} color="text.secondary"
                                sx={{ mb: 1, display: 'block', letterSpacing: 0.3 }}>
                                PORTE DA MARCA
                            </Typography>
                            <Box display="flex" gap={1} flexWrap="wrap">
                                {TIER_OPTIONS.map(opt => (
                                    <Box
                                        key={opt.value}
                                        onClick={() => setTierProfile(opt.value)}
                                        sx={{
                                            flex: 1, minWidth: 90, p: 1.5, borderRadius: 1.5,
                                            border: '1px solid',
                                            borderColor: tierProfile === opt.value ? 'primary.main' : 'divider',
                                            bgcolor: tierProfile === opt.value
                                                ? `${selectedColor}18`
                                                : 'transparent',
                                            cursor: 'pointer',
                                            textAlign: 'center',
                                            transition: 'all 0.2s',
                                            '&:hover': { borderColor: 'primary.light' }
                                        }}
                                    >
                                        <Typography variant="body2" fontWeight={600}
                                            color={tierProfile === opt.value ? 'primary.main' : 'text.primary'}>
                                            {opt.label}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {opt.desc}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>

                        {/* Keywords */}
                        <Box mb={3}>
                            <Typography variant="caption" fontWeight={600} color="text.secondary"
                                sx={{ mb: 0.5, display: 'block', letterSpacing: 0.3 }}>
                                TERMOS PARA MONITORAR{' '}
                                <Typography component="span" variant="caption" color="text.disabled" fontWeight={400}>
                                    (opcional — pode configurar depois)
                                </Typography>
                            </Typography>
                            <TextField
                                fullWidth
                                placeholder="Ex: mensure, mensure.com.br..."
                                value={keywordInput}
                                onChange={e => setKeywordInput(e.target.value)}
                                onKeyDown={e => {
                                    if (e.key === 'Enter' || e.key === ',') {
                                        e.preventDefault();
                                        handleAddKeyword();
                                    }
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <Button size="small" onClick={handleAddKeyword} sx={{ minWidth: 36 }}>
                                                <AddIcon fontSize="small" />
                                            </Button>
                                        </InputAdornment>
                                    )
                                }}
                                helperText="Pressione Enter para adicionar"
                                sx={{
                                    mb: 1.5,
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 1.5,
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'primary.main',
                                        }
                                    },
                                    '& .MuiOutlinedInput-notchedOutline legend': { display: 'none' },
                                    '& .MuiOutlinedInput-notchedOutline': { top: 0 },
                                }}
                            />
                            <Box display="flex" flexWrap="wrap" gap={0.75}>
                                {keywords.map(kw => (
                                    <Chip
                                        key={kw}
                                        label={kw}
                                        size="small"
                                        onDelete={() => handleRemoveKeyword(kw)}
                                        deleteIcon={<CloseIcon />}
                                        color="primary"
                                        variant="outlined"
                                        sx={{ fontWeight: 600 }}
                                    />
                                ))}
                            </Box>
                        </Box>

                        {/* Botão principal */}
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={loading || !brandName.trim()}
                            endIcon={!loading && <NextIcon />}
                            sx={{
                                mt: 1, mb: 2,
                                borderRadius: 1.5,
                                fontWeight: 500,
                                py: 1.2,
                                background: primaryGradient || 'primary.main',
                                '&:hover': {
                                    background: primaryGradient || 'primary.dark',
                                    filter: primaryGradient ? 'brightness(0.9)' : 'none',
                                },
                                '&.Mui-disabled': { opacity: 0.45 }
                            }}
                        >
                            {loading ? <CircularProgress size={20} color="inherit" /> : 'Entrar na Plataforma'}
                        </Button>

                        <Box textAlign="center">
                            <Typography variant="body2" color="text.secondary">
                                Já possui marcas configuradas?{' '}
                                <Link
                                    onClick={() => window.location.href = '/'}
                                    sx={{ color: 'primary.light', fontWeight: 600, cursor: 'pointer' }}
                                    variant="body2"
                                >
                                    Entrar direto
                                </Link>
                            </Typography>
                        </Box>
                    </Box>
                )}
            </Paper>
        </Box>
    );
};

export default OnboardingPage;
